import { CategoryKey, CATEGORY_TOKENS, CategoryToken } from '@utils/categoryTokens';

/**
 * Сворачивает тег уведомления с бэка (enum `NotificationType` → `<домен>.<объект>.<действие>`,
 * либо маркер `reminder` от плагина MailRemindersPlugin) в UI-категорию единой палитры.
 *
 * Возвращает `null`, если тег отсутствует или не распознан (легаси-уведомления) — тогда
 * чип категории просто не показывается.
 */
export function categoryFromTag(tag?: string | null): CategoryKey | null {
    if (!tag) return null;
    const t = tag.toLowerCase();

    // Доступ поддержки к учётной записи (вход «от имени»): support.access и будущие support.*.
    if (t === 'support' || t.startsWith('support.')) return 'support';

    // Итоги фоновых задач: загрузка из файла (job.loadFromFile.succeeded|failed) и построение отчёта (report.*).
    if (t.startsWith('job.') || t.startsWith('report.')) return 'job';

    // Назначение и снятие заместителя или уполномоченного.
    if (t.startsWith('substitution.')) return 'substitution';

    // Новый или обновлённый документ раздела «Документация» (documentation.updated).
    if (t.startsWith('documentation.')) return 'documentation';

    // Напоминание — отдельный класс, маркер от плагина (не из enum NotificationType).
    if (t === 'reminder' || t.startsWith('reminder.')) return 'reminder';

    // Сроки (duetime.* и modified.*.duetime): просрочка → Просрочено, иначе Срок.
    if (t.includes('duetime')) return /elapsed/.test(t) ? 'overdue' : 'deadline';

    // Назначение роли: роль = тип активности.
    if (t.startsWith('assignment.')) {
        if (t.startsWith('assignment.approver.')) return 'approval';
        if (t.startsWith('assignment.confirmer.')) return 'confirmation';
        if (t.startsWith('assignment.viewer.')) return 'review';
        if (t.startsWith('assignment.observer.') || t.startsWith('assignment.docobserver.')) return 'control';
        if (t.startsWith('assignment.exceptionperformer.')) return 'exception';
        if (
            t.startsWith('assignment.doccreator.') ||
            t.startsWith('assignment.docregistrator.') ||
            t.startsWith('assignment.docmainperformer.')
        ) {
            return 'doc';
        }
        // mainperformer / coperformer (и прочее) — Поручение.
        return 'task';
    }

    // События маршрута/процесса.
    if (t.startsWith('action.process.')) return 'route';

    // События документа: контроль доступа → Контроль, остальное → Документ.
    if (t.startsWith('action.doc.control.')) return 'control';
    if (t.startsWith('action.doc.')) return 'doc';

    // Исходы согласования/утверждения — до общего правила по action.activity.*.
    if (t.startsWith('action.confirmer.')) return 'confirmation';
    if (/^action\.activity\.(approved|refused|reworked|revoked)$/.test(t)) return 'approval';
    if (t === 'action.activity.viewed') return 'review';
    if (t === 'action.activity.control.returned') return 'control';

    // Прочий ход исполнения (accepted/completed/finished/resolved/rejected/writelog).
    if (t.startsWith('action.activity.')) return 'progress';

    // Изменение текста поручения (modified.*.text; срок уже обработан выше).
    if (t.startsWith('modified.')) return 'doc';

    return null;
}

/** Токен (цвет/иконка/метка) категории уведомления по его тегу, либо `null`. */
export function categoryTokenFromTag(tag?: string | null): CategoryToken | null {
    const key = categoryFromTag(tag);
    return key ? CATEGORY_TOKENS[key] : null;
}

/**
 * Обязательные категории — назначенные вам работы, доступ поддержки и замещение. Их нельзя заглушить mute по документу
 * (enforcement в ядре: NeedSend пропускает mute для обязательных). Зеркало
 * `CategoryMapper.IsObligatory` на бэке — при правке синхронизировать.
 */
const OBLIGATORY_CATEGORIES: ReadonlySet<CategoryKey> = new Set<CategoryKey>([
    'task',
    'approval',
    'confirmation',
    'review',
    'exception',
    'support',
    'substitution',
]);

/**
 * Можно ли заглушить уведомление кнопкой «Не уведомлять по документу»: информационные категории
 * (ход, маршрут, сроки, изменения…) — да; назначенные работы (обязательные) — нет.
 */
export function isMuteableTag(tag?: string | null): boolean {
    const key = categoryFromTag(tag);
    return key != null && !OBLIGATORY_CATEGORIES.has(key);
}
